import {
  CryptoService,
  CryptoUtilities,
  CryptoWorkerService,
  encryptionServiceInstance,
} from 'securedrive-crypto';
import API_CALL from '../API';
import { DownloadService } from '../download/download.service';
import { getB64_EZ_Key } from '../request/RequestHelpers';
import { SessionService } from '../session/session.service';
import { ShareEntryResponse } from '../share/ShareService';
/**
 * Service for managing encryption-related operations with the backend.
 */
export async function initialize(
  b64EncryptedMasterKey: string,
  b64EncryptedKeychain: string,
  recovery_data?: string
): Promise<void> {
  const data: any = {
    enc_mk: b64EncryptedMasterKey,
    enc_keychain: b64EncryptedKeychain,
  };
  if (recovery_data) {
    data.recovery_data = recovery_data;
  }

  try {
    await API_CALL({
      url: `/encryption`,
      method: 'POST',
      data,
    });
  } catch (error) {
    console.error('Initialization failed:', error);
    throw error;
  }
}

// Funzione per resettare il sistema di crittografia
export async function resetMasterKey(): Promise<void> {
  try {
    await API_CALL({
      url: `/encryption`,
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Reset failed:', error);
    throw error;
  }
}

// Funzione per recuperare la chiave crittografata
export async function getEncryptedKeychain(): Promise<string> {
  try {
    const response = await API_CALL({
      url: `/encryption/enc_keychain`,
      method: 'GET',
    });
    return response.enc_keychain;
  } catch (error) {
    console.error('Failed to get encrypted keychain:', error);
    throw error;
  }
}

// Funzione per recuperare la chiave master crittografata
export async function getEncryptedMasterKey(): Promise<{
  enc_mk: string;
  set_on: string;
}> {
  try {
    const response = await API_CALL({
      url: `/encryption/enc_mk`,
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    if (typeof error === 'string' && error.includes('404')) {
      console.info('Master key not found');
      return { enc_mk: '', set_on: '' };
    }
    console.error('Failed to get encrypted master key:', error);
    throw error;
  }
}

// Funzione per aggiornare la chiave master crittografata
export async function updateEncryptedMasterKey(
  b64EncryptedMasterKey: string
): Promise<void> {
  try {
    await API_CALL({
      url: `/encryption/enc_mk`,
      method: 'PUT',
      data: { enc_mk: b64EncryptedMasterKey },
    });
  } catch (error) {
    console.error('Failed to update encrypted master key:', error);
    throw error;
  }
}

// Funzione per recuperare IV e DEK crittografati per i percorsi file specificati
export async function getIVandENC_DEK(
  filePaths: string[]
): Promise<[string, string]> {
  try {
    const response = await API_CALL({
      url: `/encryption/ivs`,
      method: 'POST',
      data: filePaths,
    });
    return response[filePaths[0]];
  } catch (error) {
    console.error('Failed to get IVs and DEKs:', error);
    throw error;
  }
}

// Funzione per controllare se il recupero della crittografia è abilitato per l'utente
export async function getEncryptionRecovery(): Promise<boolean> {
  try {
    const response = await API_CALL({
      url: `/encryption/recovery`,
      method: 'GET',
    });
    return response.data.enabled;
  } catch (error) {
    console.error('Failed to get encryption recovery status:', error);
    throw error;
  }
}

// Funzione per eseguire l'opt-in per il recupero della crittografia
export async function postEncryptionRecovery(
  b64CypheredBinaryOutput: string
): Promise<void> {
  try {
    await API_CALL({
      url: `/encryption/recovery`,
      method: 'POST',
      data: { recovery_data: b64CypheredBinaryOutput },
    });
  } catch (error) {
    console.error('Failed to post encryption recovery:', error);
    throw error;
  }
}

/**
 * Funzione per cambiare la passphrase dell'utente
 * @param oldPassphrase - Vecchia passphrase
 * @param newPassphrase - Nuova passphrase
 */
export async function changePassphrase(
  oldPassphrase: string,
  newPassphrase: string
): Promise<void> {
  try {
    const username = (
      await SessionService.getSession()
    ).provisioning_id.toLowerCase();

    // Ottieni Master Key con vecchia passphrase
    const { enc_mk } = await getEncryptedMasterKey();

    const masterKey = await encryptionServiceInstance.decryptMasterKey(
      username,
      oldPassphrase,
      enc_mk
    );

    // Cripta Master Key con nuova passphrase
    const b64EncryptedMasterKey =
      await encryptionServiceInstance.encryptMasterKey(
        username,
        newPassphrase,
        masterKey
      );

    // Aggiorna la Master Key nel backend
    return updateEncryptedMasterKey(b64EncryptedMasterKey);
  } catch (error) {
    console.error('Failed to change passphrase:', error);
    throw error;
  }
}

export const generateSharePassword = async (
  path: string,
  sharing_password: string
): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      const cryptoService = CryptoService.getInstance();
      const cryptoUtilities = CryptoUtilities;

      let sharedEncDekB64 = sharing_password;

      const ez_key = await getB64_EZ_Key();

      // Ottieni IV e ENC_DEK
      const [iv, enc_dek] = await getIVandENC_DEK([path]);

      const dek = await cryptoService.decryptCBC(
        cryptoUtilities.base64ToArrayBuffer(ez_key),
        cryptoUtilities.base64ToArrayBuffer(enc_dek)
      );
      // Ottieni la chiave EZ key usando username e password

      // Converti la password in ArrayBuffer
      const keyArrayBuffer =
        cryptoUtilities.UTF8StringToArrayBuffer(sharing_password);

      // Deriva la chiave usando PBKDF2
      const derivedKey = await cryptoService.getPbkdf2KeyDerivation(
        sharing_password,
        cryptoUtilities.UTF8StringToArrayBuffer(sharing_password)
      );

      const sharedEncDek = await cryptoService.encryptCBC(
        derivedKey,
        dek,
        cryptoUtilities.base64ToArrayBuffer(iv)
      );

      sharedEncDekB64 = cryptoUtilities.arrayBufferToBase64(sharedEncDek);

      resolve(sharedEncDekB64);
    } catch (error) {
      console.error('Failed to generate share password:', error);
      reject(error);
    }
  });
};

/**
 * Verifica la password e ottiene la chiave di decrittazione
 * @param fileShare Informazioni del file condiviso
 * @param sharing_password Password di condivisione
 * @returns La chiave di decrittazione (DEK) o undefined se la password è errata
 */
export const verifySharingPassword = async (
  fileShare: ShareEntryResponse,
  sharing_password: string
): Promise<ArrayBuffer | undefined> => {
  if (!fileShare.shared_enc_dek) return undefined;

  const cryptoService = CryptoService.getInstance();
  const cryptoUtilities = CryptoUtilities;

  try {
    // Deriva la chiave dalla password
    const derivedKey = await cryptoService.getPbkdf2KeyDerivation(
      sharing_password,
      cryptoUtilities.UTF8StringToArrayBuffer(sharing_password)
    );

    // Decrittografa il DEK
    const dekB64 = await cryptoService.decryptCBC(
      derivedKey,
      cryptoUtilities.base64ToArrayBuffer(fileShare.shared_enc_dek)
    );

    return dekB64;
  } catch (error) {
    console.error('Errore nella verifica della password:', error);
    return undefined;
  }
};

/**
 * Decrittografa un file condiviso utilizzando la chiave DEK
 * @param fileShare Informazioni del file condiviso
 * @param dek Chiave di decrittazione
 * @param downloadedFile Contenuto cifrato gia' scaricato dal chiamante. Se
 * omesso il file viene scaricato qui: passarlo evita di scaricarlo due volte
 * quando chi chiama ha gia' il payload (es. per mostrare il progresso)
 * @returns Il blob del file decrittografato
 */
export const decryptSharedFileWithDek = async (
  fileShare: ShareEntryResponse,
  dek: ArrayBuffer,
  downloadedFile?: ArrayBuffer
): Promise<Blob> => {
  const workerService = CryptoWorkerService.getInstance();

  try {
    // Scarica il file solo se il chiamante non lo ha gia'
    const arrayBufferFile =
      downloadedFile ??
      (await DownloadService.downloadFileToArrayBuffer(
        fileShare.download_url,
        console.log
      ));

    // Decrittografa il file
    const decriptedFile = await workerService.decryptFile(dek, arrayBufferFile);

    // Crea il blob
    return new Blob([decriptedFile.data], {
      type: 'application/octet-stream',
    });
  } catch (error) {
    console.error('Errore nella decrittazione del file:', error);
    throw error;
  }
};

/**
 * Decrittografa un file condiviso utilizzando la password
 * @param fileShare Informazioni del file condiviso
 * @param sharing_password Password di condivisione
 * @returns Il blob del file decrittografato
 */
export const decryptSharedFile = async (
  fileShare: ShareEntryResponse,
  sharing_password: string
): Promise<Blob | undefined> => {
  try {
    // Verifica la password e ottieni il DEK
    const dek = await verifySharingPassword(fileShare, sharing_password);
    if (!dek) return undefined;

    // Decrittografa il file con il DEK
    return await decryptSharedFileWithDek(fileShare, dek);
  } catch (error) {
    console.error('Errore nella decrittazione del file condiviso:', error);
    return undefined;
  }
};

export const decryptFile = async (
  encryption_dek: string,

  arrayBufferFile: ArrayBuffer
) => {
  const workerService = CryptoWorkerService.getInstance();

  const b64EzKey = await getB64_EZ_Key();

  const ez_key = CryptoUtilities.base64ToArrayBuffer(b64EzKey);

  const arrayEncDek = CryptoUtilities.base64ToArrayBuffer(encryption_dek);
  let decryptedDek = await encryptionServiceInstance.decryptDek(
    ez_key,
    arrayEncDek
  );

  const decriptedFile = await workerService.decryptFile(
    decryptedDek,
    arrayBufferFile
  );
  const blob = new Blob([decriptedFile.data], {
    //type: 'application/octet-stream',
  });

  return blob;
};

export const EncryptionService = {
  initialize,
  resetMasterKey,
  getEncryptedKeychain,
  getEncryptedMasterKey,
  updateEncryptedMasterKey,
  getIVandENC_DEK,
  getEncryptionRecovery,
  postEncryptionRecovery,
  changePassphrase,
  generateSharePassword,
  decryptSharedFile,
  decryptSharedFileWithDek,
  decryptFile,
  verifySharingPassword,
};
